import React from 'react';
import { Brain } from 'lucide-react';
import { Assistant } from '@/types';
import skautoIcon from '@/assets/DriveSource Favicon White.svg';
import homesIcon from '@/assets/HomeSource Favicon White.svg';

interface RenderAssistantIconProps {
    assistant: Assistant | null;
    className?: string;
    iconColor?: string;
    useGradientColor?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

const getSizeClasses = (size: 'sm' | 'md' | 'lg' = 'md') => {
    switch (size) {
        case 'sm':
            return 'w-3 h-3 sm:w-4 sm:h-4';
        case 'md':
            return 'w-6 h-6';
        case 'lg':
            return 'w-12 h-12';
        default:
            return 'w-6 h-6';
    }
};

export const renderAssistantIcon = ({
                                        assistant,
                                        className = '',
                                        iconColor = '#22d3ee',
                                        useGradientColor = false,
                                        size = 'md'
                                    }: RenderAssistantIconProps): React.ReactNode => {
    const sizeClasses = getSizeClasses(size);

    // Special case for autoGPTforme assistant
    if (assistant?.name === 'autoGPTforme' || assistant?.code === 'autoGPTforme' || assistant?.id === 'autoGPTforme') {
        return (
            <img
                src={skautoIcon}
                alt="Skauto Assistant"
                className={`${sizeClasses} object-contain flex-shrink-0 ${className}`}
            />
        );
    }

    // Special case for homesGPTforme assistant
    if (assistant?.name === 'homesGPTforme' || assistant?.code === 'homesGPTforme' || assistant?.id === 'homesGPTforme') {
        return (
            <img
                src={homesIcon}
                alt="Homes Assistant"
                className={`${sizeClasses} object-contain flex-shrink-0 ${className}`}
            />
        );
    }

    if (!assistant) {
        return (
            <div style={{ color: iconColor }} className={className}>
                <Brain className={sizeClasses} />
            </div>
        );
    }

    if (assistant.icon?.kind === 'component') {
        if (typeof assistant.icon.component === 'string') {
            return (
                <div style={{ color: iconColor }} className={className}>
                    <Brain className={sizeClasses} />
                </div>
            );
        }
        const IconComponent = assistant.icon.component;
        return (
            <div style={{ color: iconColor }} className={className}>
                <IconComponent className={sizeClasses} />
            </div>
        );
    } else if (assistant.icon?.kind === 'image') {
        return (
            <div className={`relative ${className}`}>
                <img
                    src={assistant.icon.src}
                    alt={assistant.name || 'Assistant'}
                    className={`${sizeClasses} object-cover rounded-sm`}
                    onError={(e) => {
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.querySelector('.fallback-icon')) {
                            const fallbackIcon = document.createElement('div');
                            fallbackIcon.className = 'fallback-icon';
                            fallbackIcon.innerHTML = `<svg class="${sizeClasses}" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: ${iconColor}"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path></svg>`;
                            parent.appendChild(fallbackIcon);
                        }
                    }}
                />
                {useGradientColor && (
                    <div
                        className={`absolute inset-0 ${sizeClasses} rounded-sm opacity-20 mix-blend-multiply`}
                        style={{ backgroundColor: iconColor }}
                    />
                )}
            </div>
        );
    }

    return (
        <div style={{ color: iconColor }} className={className}>
            <Brain className={sizeClasses} />
        </div>
    );
};